import type { GroupChip, VehicleDeviceRef, VehicleOut } from '@/lib/api/types';
import { formatNumber } from '@/lib/format';
import { tp } from '@/lib/i18n';
import type { ComboOption } from '@/ui';
import type { Grouping } from './api';

/** The devices fitted to a vehicle. */
export function vehicleDevices(v: Pick<VehicleOut, 'devices'>): VehicleDeviceRef[] {
  return v.devices ?? [];
}

/** The ascending sort fields of a generated sort type (`name` of `name | -name`). */
export type SortField<S extends string> = Exclude<S, `-${string}`>;

/** "1,234 vehicles" from a list's total; "≈ 12,300 vehicles" when the server estimated it. */
export function totalLabel(total: { total: number; estimated: boolean } | null, one: string, other: string): string | undefined {
  if (!total) return undefined;
  const text = tp(total.total, one, other, { count: formatNumber(total.total) });
  return total.estimated ? `≈ ${text}` : text;
}

/** A column id → server sort field map, applied to a URL sort (`col` / `-col`). */
export function serverSort<F extends string>(sort: string | undefined, fields: Readonly<Record<string, F>>): F | `-${F}` | undefined {
  if (!sort) return undefined;
  const desc = sort.startsWith('-');
  const field = fields[desc ? sort.slice(1) : sort];
  if (!field) return undefined;
  return desc ? `-${field}` : field;
}

/** Only columns the server can sort by are sortable (sorting happens on the server). */
export function withServerSort<T extends { id: string; sortable?: boolean }>(columns: T[], fields: Readonly<Record<string, string>>): T[] {
  return columns.map((c) => ({ ...c, sortable: c.sortable !== false && c.id in fields }));
}

export function chipItems(groups: GroupChip[] | undefined): { id: string; label: string; color?: string | null; title?: string }[] {
  return (groups ?? []).map((g) => ({ id: g.id, label: g.name, color: g.color, title: `${g.dimension_label}: ${g.name}` }));
}

/** Group filter options for one kind of member, grouped under their grouping. */
export function groupOptions(groupings: Grouping[] | undefined, appliesTo: 'vehicle' | 'driver'): ComboOption[] {
  return (groupings ?? [])
    .filter((d) => d.applies_to === appliesTo)
    .flatMap((d) => d.groups.map((g) => ({ value: g.id, label: g.name, group: d.label, color: g.color, description: null })));
}

export function projectName(projects: { id: string; name: string }[], id: string | null | undefined): string | null {
  return projects.find((p) => p.id === id)?.name ?? null;
}
